import { useEffect, useMemo, useState } from 'react';
import {
  onSnapshot,
  query,
  type Query,
  type QueryConstraint,
  collection,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useActiveTenantId } from '../lib/tenant';

interface State<T> {
  data: T[];
  loading: boolean;
  error: Error | null;
}

/** Live subscription to a collection with optional constraints.
 *  Docs are returned as { id, ...fields }. */
export function useCollection<T extends { id: string }>(
  path: string,
  constraints: QueryConstraint[] = [],
  enabled = true,
): State<T> {
  const [state, setState] = useState<State<T>>({
    data: [],
    loading: enabled,
    error: null,
  });

  // Phase 5D — the active tenant, read reactively. Belt-and-suspenders
  // against switchActiveTenant: most callers already rebuild `constraints`
  // with a fresh tenantWhere() when the active tenant changes (their own
  // useMemo depends on useActiveTenantId() too — see src/hooks/useUnits.ts
  // etc.), but `constraints.map(c => c.type)` below can't tell a WHERE
  // clause's own VALUE apart from a stale one of the same shape (e.g. two
  // memberships that are both unscoped admin). Forcing a resubscribe here
  // whenever the active tenant changes closes that gap for every current
  // and future caller, not just the ones that happen to also change shape.
  const activeTenantId = useActiveTenantId();

  // Stable key so we don't resubscribe every render.
  const key = useMemo(
    () => JSON.stringify(constraints.map((c) => c.type)) + '|' + path + '|' + String(enabled) + '|' + activeTenantId,
    [constraints, path, enabled, activeTenantId],
  );

  useEffect(() => {
    if (!enabled) {
      setState({ data: [], loading: false, error: null });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    const q: Query = constraints.length
      ? query(collection(db, path), ...constraints)
      : collection(db, path);
    return onSnapshot(
      q,
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as T);
        setState({ data, loading: false, error: null });
      },
      (error) => setState({ data: [], loading: false, error }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}
